import { Button, PageLoader } from '@cfm-media/pwa-ui-components';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import useSWRMutation from 'swr/mutation';

import { useStore } from '../core/store';
import { useTranslation } from '../core/utils/translations';
import { useContactInformationForm } from '../hooks/useContactInformationForm';
import { urls } from '../urls';
import { defaultMutator } from '../utils/default-fetchers';
import { ContentWrapper } from './content-wrapper';
import { HeadlessModal } from './headless-modal';
import { Modal } from './modal';

export const ContactInformationForm = observer(() => {
  const { t } = useTranslation('contact-information-form');

  const { BookingStore } = useStore();
  const { showForm, setShowForm } = useContactInformationForm();
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);

  const {
    handleSubmit,
    register,
    formState: { errors: formErrors },
  } = useForm({
    defaultValues: {
      clientEmail: '',
      clientMobileNumber: '',
    },
  });

  const {
    trigger: submitForm,
    isMutating,
    error,
  } = useSWRMutation(urls.api.clientContacts, defaultMutator);

  const onSubmit = async (data) => {
    try {
      await submitForm(data);
    } catch (error) {
      console.error('Submission error:', error.message);
    }

    setShowSubmissionModal(true);
  };

  const onClose = () => {
    setShowSubmissionModal(false);
    setShowForm(false);
    BookingStore.booking.fetch();
  };

  if (isMutating) {
    return <PageLoader variant="absolute" info={t('submit-loader-text')} />;
  }

  return (
    <>
      {showSubmissionModal ? (
        <Modal
          show={showSubmissionModal}
          type={error ? 'error' : 'success'}
          headline={t(
            error
              ? 'contact-sent-error-modal-headline'
              : 'contact-sent-success-modal-headline',
          )}
          text={t(
            error
              ? 'contact-sent-error-modal-body'
              : 'contact-sent-success-modal-body',
          )}
          buttonText={t('contact-modal-button-text')}
          onClose={onClose}
          fullWidth
        />
      ) : (
        <HeadlessModal isOpen={showForm} close={setShowForm}>
          <ContentWrapper>
            <div className="mb-3 text-xl text-brand-1 font-semibold uppercase">
              {t('contact-information-form-title')}
            </div>

            <p>{t('contact-information-form-text')}</p>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 mt-4 mb-4">
                <div>
                  <label
                    htmlFor="email"
                    className="text-sm font-semibold text-gray-700"
                  >
                    {`${t('email-input-label')}`}
                  </label>
                  <input
                    className="w-full rounded-md"
                    type="email"
                    {...register('clientEmail', {
                      required: `${t('error-message-email')}`,
                    })}
                  />
                  {formErrors?.clientEmail && (
                    <div className="text-red-600 text-sm mt-2">
                      {formErrors.clientEmail.message}
                    </div>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="phone"
                    className="text-sm font-semibold text-gray-700"
                  >
                    {`${t('phone-input-label')}`}
                  </label>
                  <input
                    className="w-full rounded-md"
                    type="number"
                    {...register('clientMobileNumber', {
                      required: `${t('error-message-phone')}`,
                    })}
                  />
                  {formErrors?.clientMobileNumber && (
                    <div className="text-red-600 text-sm mt-2">
                      {formErrors.clientMobileNumber.message}
                    </div>
                  )}
                </div>
              </div>

              <Button
                buttonType="submit"
                variant="primary"
                title={t('submit')}
                className="w-full"
                disabled={isMutating}
              />
            </form>
          </ContentWrapper>
        </HeadlessModal>
      )}
    </>
  );
});
